import type {
  AssignableAdminUser,
  FileAssignmentRecord,
} from '../../../../domain/src/lib/repository/file-assignment.model';
import { isDipAssignablePath } from './repository-assignment-path.util';

export interface RepositoryAssignmentGroup {
  /** Id de la asignación (una fila = un usuario). */
  id: string;
  filePath: string;
  fileName: string;
  isDirectory: boolean;
  assignmentIds: string[];
  users: AssignableAdminUser[];
  roles: string[];
  expiresAt: string | null;
  isExpired: boolean;
  oaisCompliant: boolean;
}

/**
 * Una fila por asignación (usuario + ruta), no agrupadas por path.
 * Así se puede desasignar a un usuario sin afectar a otros en la misma carpeta.
 */
export function groupFileAssignments(
  assignments: FileAssignmentRecord[],
  usersById: Map<string, AssignableAdminUser>,
): RepositoryAssignmentGroup[] {
  const seenIds = new Set<string>();
  const unique = assignments.filter((a) => {
    if (!a.id || seenIds.has(a.id)) {
      return false;
    }
    seenIds.add(a.id);
    return true;
  });

  const groups: RepositoryAssignmentGroup[] = unique.map((row) => {
    const filePath = row.file_path || '';
    const parts = filePath.split('/').filter(Boolean);
    const fileName = parts[parts.length - 1] || filePath;
    const users: AssignableAdminUser[] = [];
    if (row.user_id) {
      const user = usersById.get(row.user_id);
      if (user) {
        users.push(user);
      }
    }
    const roles = row.role ? [row.role] : [];
    let expiresAt: string | null = null;
    let isExpired = false;
    if (row.expires_at) {
      const ts = new Date(row.expires_at).getTime();
      if (Number.isFinite(ts)) {
        expiresAt = new Date(ts).toISOString();
        isExpired = ts <= Date.now();
      }
    }

    return {
      id: row.id,
      filePath,
      fileName,
      isDirectory: row.is_directory === true,
      assignmentIds: [row.id],
      users,
      roles,
      expiresAt,
      isExpired,
      oaisCompliant: isDipAssignablePath(filePath),
    };
  });

  return groups.sort((a, b) => {
    const byPath = a.filePath.localeCompare(b.filePath, 'es');
    if (byPath !== 0) {
      return byPath;
    }
    const aUser = a.users[0]?.full_name || a.users[0]?.email || '';
    const bUser = b.users[0]?.full_name || b.users[0]?.email || '';
    return aUser.localeCompare(bUser, 'es');
  });
}
